import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import Layout from '../../components/Layout';
import '../../styles/member-cards.css';

interface MemberProps {
	name: string;
	img: JSX.Element;
	position?: string;
	about: string;
	social: {
		li: string;
		mail?: string;
		rg?: string;
	};
}

const size = 36;

const Social = ({ li, mail, rg }: { mail?: string; li: string; rg?: string }) => (
	<div className='member__social'>
		<a href={`https://www.linkedin.com/in/${li}`} target='_blank'>
			<svg
				role='link'
				viewBox='0 0 24 24'
				width={size}
				height={size}
				xmlns='http://www.w3.org/2000/svg'
				fill='#0a66c2'
			>
				<title>LinkedIn</title>
				<path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.28[...]'
				/>
			</svg>
		</a>

		{mail && (
			<a href={`mailto:${mail}`} target='_blank'>
				<svg
					xmlns='http://www.w3.org/2000/svg'
					width={size + 8}
					height={size + 10}
					viewBox='0 0 24 24'
					role='link'
					fill='#a0a0a0'
				>
					<path d='M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4.7-8 5.334L4 8.7V6.297l8 5.333 8-5.333V8.7z'></path>
				</svg>
			</a>
		)}

		{rg && (
			<a href={`https://www.researchgate.net/profile/${rg}`} target='_blank'>
				<svg
					role='link'
					viewBox='0 0 24 24'
					width={size}
					height={size}
					xmlns='http://www.w3.org/2000/svg'
					fill='#00CCBB'
				>
					<title>ResearchGate</title>
					<path d='M19.586 0c-.818 0-1.508.19-2.073.565-.563.377-.97.936-1.213 1.68a3.193 3.193 0 0 0-.112.437 8.365 8.365 0 0 0-.078.53 9 9 0 0 0-.05.727c-.01.282-.013.621-.013 1.016a31.121 31.123 0 0 0 .[...]'
					/>
				</svg>
			</a>
		)}
	</div>
);

const Member = ({ about, img, name, social: { li, mail, rg }, position }: MemberProps) => (
	<div className='br member px-3'>
		{img}
		<div className='member__body'>
			<h2 className='member__title raleway-700'>{name}</h2>
			{position && <span className='member__subtitle text-secondary'>{position}</span>}
			<div className='member__about'>{about}</div>
			<Social li={li} mail={mail} rg={rg} />
		</div>
	</div>
);

const AboutTeamPage = () => (
	<Layout title='About the Team'>
		<h1 className='raleway-700 display-5 my-3 px-3'>2026 Student Board</h1>
		<div className='row mb-5 justify-content-evenly align-items-start'>
			<Member
				name='Anna Wadhwa'
				position='President'
				about={`Anna Wadhwa is an MD-PhD student at Harvard Medical School and MIT AeroAstro in the Aerospace Physiology Lab (APL). 
				She received her BS in Biochemistry from the New Jersey Institute of Technology in 2021, summa cum laude. 
				Her prior experience spans various NASA centers, including: immune health at Johnson Space Center, fatigue countermeasures at Ames Research Center, and satellite algorithm development with Goddard
				Institute for Space Studies. 
				Since starting at Harvard, she has been studying the use of artificial gravity for bone health with the NASA/JAXA MHU-8 mission. 
				Now at MIT, she is also working to expand diagnostic capabilities in space with the SpaceX FRAM2 SpaceXray mission. 
				She hopes to study physiological risks and develop countermeasures for astronaut health in lunar/Martian environments. 
				Outside the lab, Anna enjoys running, hiking, skiing, and annoying her cat Laika.`}
				img={
					<StaticImage
						src='../../images/team/Wadhwa_Headshot.26.png'
						alt='Anna Wadhwa'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{
					li: 'imastroanna',
				}}
			/>

			<Member
				name='Simone Lilavois'
				position='President-Elect'
				about={`Simone Lilavois is an undergraduate physics student at Amherst College interested in using astronomical data to investigate fundamental questions in particle physics and cosmology. 
				She is currently conducting theoretical research at the Center for Computational Astrophysics at the Flatiron Institute, where she uses numerical simulations to investigate the long-term 
				dynamical evolution of multi-planetary systems. She also works with absolute and relative astrometry, as well as radial velocity data, to constrain the orbits and masses of binary star systems within 20 parsecs. 
				Lastly, she is an intern with the Space Agriculture Laboratory Analysis Database (SALAD), where she uses BioSim, a simulation of a Bioregenerative Life Support System, to establish baseline plant production metrics for future Moon and Mars missions. 
				Simone plans to pursue a Ph.D. in astrophysics and a career in academia.`}
				img={
					<StaticImage
						src='../../images/team/Simone_25.jpeg'
						alt='Simone Lilavois'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'simonelilavois' }}
			/>

			<Member
				name='McKenna Taylor'
				position='Education & Outreach Coordinator'
				about={`McKenna is a graduate student pursuing a Master’s degree in Cell and Molecular Biology. 
				She has completed two Bachelor’s degrees in Astronomy & Astrophysics and Astrobiology. 
				McKenna is a 2024 Astronaut Scholar with the Astronaut Scholarship Foundation and her research focused 
				on the use of bacteria derived from cultures taken from the International Space Station to determine 
				their potential use for plant growth-promotion in simulated microgravity. `}
				img={
					<StaticImage
						src='../../images/team/Mckenna_26.JPEG'
						alt='McKenna Taylor'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'mckenna-taylor3' }}
			/>

			<Member
				name='Tao Sheng'
				position='Membership and Community Engagement Chair'
				about={`Tao Sheng is a medical student at the University of Pittsburgh School of Medicine. He received his B.S. in Computational Biology from the University of Pittsburgh, and as a junior, trained as a research associate in the NASA Ames Space Life Sciences Training Program. After graduating, he conducted deep learning-based bone lesion detection research at the NIH as an IRTA fellow.
				At Pitt, he is pursuing his interests in personalized medicine and machine learning across two projects: studying kaempferol as a hepatoprotective countermeasure for spaceflight and applying machine learning to cardio-oncology outcomes research. He aims to pursue a career integrating aerospace medicine with internal and preventive medicine. Outside of school, Tao enjoys wildlife photography around western Pennsylvania, endurance running, and chess.`}
				img={
					<StaticImage
						src='../../images/team/Tao_2026.jpg'
						alt='Tao Sheng'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'taos' }}
			/>
			<Member
				name='Sage Cohen'
				position='Social Media Coordinator'
				about={`Sage is a recent Florida Gulf Coast University graduate of '24 & '26 where she earned two Bachelor’s degrees 
				in Biology with a Chemistry Minor & Clinical Laboratory Science. Her research, under Neuroscientist Dr. Sherri Emer, 
				focuses on the physiological effects of microgravity and spaceflight on human sensory systems using immunohistochemistry. 
				Sage has worked with recent Rodent Research (RR) specimens from the International Space Station through NASA Space Biology 
				Biospecimen Sharing Program and archived RR specimens through NASA Ames Life Sciences Data Archive. She has just completed her 
				clinical training rotations and will become ASCP MLS certified this Summer 2026. She plans to continue her studies to become a physician scientist. 
				Outside the lab, Sage enjoys reading, expressing herself through art, and playing with her cats, Venus, Mercury, Nova, and Pluto. `}
				img={
					<StaticImage
						src='../../images/team/Sage_26.png'
						alt='Sage Cohen'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'sageinstem' }}
			/>
			<Member
				name='Nick Miklave'
				position='Secretary and Treasurer'
				about={`Nick is a Ph.D Candidate for Environmental and Evolutionary Biology at the University of Louisiana at Lafayette, and has 
				a B.S. in Physics and Astronomy from Stony Brook University. His interests include the mechanisms that plants use to interact with
				their physical environment and how these evolved mechanisms can be integrated with new technologies to overcome the challenges of 
				growing crops during spaceflight. He is a NASA Space Technology Graduate Research Fellow, developing synthetic substrates that can
				be used as a tool for general plant biology research and as a lightweight and multipurpose substrate for crop production in space environments.`}
				img={
					<StaticImage
						src='../../images/team/Nick.png'
						alt='Nick Miklave'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'nmiklave' }}
			/>

			<Member
				name='Justin Wheeler'
				position='Event Coordinator'
				about={`Justin Wheeler is a PhD student in Aerospace Engineering at the Georgia Institute of Technology. 
				His research is related to novel technology addressing issues with in-space propellant refueling. 
				He is a recipient of the National Science Foundation Graduate Research Fellowship Program (NSF GRFP) and the Georgia Tech President's fellowship awards. 
				Justin is serving as the ASGSR Event Coordinator, assisting with the planning of student-focused events at the annual ASGSR conference, as well as an event in Washington DC advocating for funding
				in the space research community.
				Outside of work, he is an avid swimmer, weightlifter, and runner.`}
				img={
					<StaticImage
						src='../../images/team/justin_wheeler-768x937.jpg'
						alt='Justin Wheeler'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'justin-wheeler88' }}
			/>

			<Member
				name='Isaiah Taylor'
				position='SECA Coordinator'
				about={`Isaiah Taylor recently earned his PhD in Molecular and Cellular Pharmacology at Stony Brook University. 
				Isaiah currently works in the Department of Orthopaedics and Rehabilitation conducting research for a NASA grant. 
				This grant's main goals are to elucidate the effects of microgravity on osteoblast-osteoclast communication and determine the effects 
				of differential gravity using the hindlimb suspension model.
				Previously, Isaiah conducted research at NIH to gain experience in the field of biochemistry and cancer, which helps with his current project.
				Isaiah specializes in cell culture of primary and secondary cells and has worked in the field of biochemistry, cancer, and orthopaedics.
				Isaiah is an exemplary and passionate researcher with an abiding interest in pursuing a career in the space and orthopedics industry. 
				In his free time, Isaiah likes to workout, read, cook and play with his cat Zoe.`}
				img={
					<StaticImage
						src='../../images/team/Isaiah-Taylor.jpg'
						alt='Isaiah Taylor'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: '' }}
			/>
		</div>
		<h2 className='raleway-700 display-6 my-3 px-3'>Social Media Team</h2>
		<div className='row justify-content-evenly align-items-start mb-5'>
			<Member
				name='Ruth Singh'
				about={`Ruth Singh is a PhD student in the Blaber lab at RPI. She has completed
							here Bachelor's in Physics and a Diploma in Astronomy and Astrophysics from University of Mumbai, India.
							She is also a Board of Trustee at 'Project Encephalon' a neuroscience organization in India. She is
							currently working as a Research Intern at Mason Lab analysing single cell omics datasets from spaceflight missions.`}
				img={
					<StaticImage
						src='../../images/team/ruth.jpg'
						alt='Ruth Singh'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'ruth-singh08', mail: 'singhr9@rpi.edu' }}
			/>

			{/* <Member
				name='Mesgana Admassu'
				about={`Mesgana Admassu is a recent graduate of Howard University, having obtained a B.S. in Biology. While
							pursuing her degree, she conducted research in her university's Department of Biology. The project
							encompassed comparing the adverse effects sustained on red maple trees from climate change. In 2021,
							she interned at NASA Ames Research Center to study wildfire plume dispersion in Northern California
							and at NASA Kennedy Space Center to study the effects of microgravity on differential gene
							expression. Mesgana is honored to be working with the ASGSR Student Board to continue exposing
							students to space studies!`}
				img={
					<StaticImage
						src='../../images/team/mesgana.jpeg'
						alt='Mesgana Admassu'
						class='member__image'
						aspectratio={1}
						formats={['auto', 'webp']}
						placeholder='blurred'
					/>
				}
				social={{ li: 'mesganaadmassu', mail: 'mesadmassu@gmail.com' }}
			/> */}
		</div>
	</Layout>
);

export default AboutTeamPage;
